import React, { useState } from 'react';
import { Container, Typography, Box, Button, DialogContentText, Checkbox, FormControlLabel } from '@mui/material';
import ScheduleCard from '../../../components/employee/services/ScheduleCard';
import {
  convertTo12Hour,
  formatDate,
} from '../../../time/timeUtils';
import { DAYS_OF_WEEK_PREFERRED_ORDER, MAX_WORK_HOURS_UPDATE_RANGE_DAYS } from '../../../constants';
import ConfirmationDialog from '../../../components/global/ConfirmationDialog';
import { EditWorkHoursViewModel } from './EditWorkHoursViewModel';
import SuccessOrErrorDialog from '../../../components/global/SuccessOrErrorDialog';
import DropdownCalendar from '../../../components/employee/components/DropDownCalendar';
import { Dayjs } from '../../../utils/dayjs-config';
import LoadingSpinner from '../../../components/global/LoadingSpinner';

const EditWorkHoursPage: React.FC = () => {

  const {
    parsedSchedule,
    dialogOpen,
    setDialogOpen,
    affectedAppointments,
    startDate,
    endDate,
    makeDefaultSchedule,
    resultDialogOpen,
    onCloseResultDialog,
    fetchWorkHoursMutation,
    onSaveMutation,
    handleScheduleChange,
    recurringBreaksByDay,
    handleRecurringBreaksChange,
    onStartDateChange,
    onEndDateChange,
    onMakeDefaultScheduleChange,
    isLoading,
    isSetUpError,
    errorMessage,
    isSaving,
  } = EditWorkHoursViewModel();


  // Pieces needed for the stepper 
  const steps = ['1', '2'];
  const [currentStep, setCurrentStep] = useState(0);

  if (isLoading)
    return <LoadingSpinner />;

  if (isSetUpError)
    return (
      // This is for setting up errors, like things we need in order to build the page.
      <Typography color="error" align="center">
        Failed to load data: {errorMessage}
      </Typography>
    );

  const shouldDisableStartDate = (date: Dayjs): boolean => {
    if (date.isAfter(endDate)) {
      return true;
    }
    return endDate.diff(date, 'day') > MAX_WORK_HOURS_UPDATE_RANGE_DAYS;
  };

  const shouldDisableEndDate = (date: Dayjs): boolean => {
    if (date.isBefore(startDate)) {
      return true;
    }
    return date.diff(startDate, 'day') > MAX_WORK_HOURS_UPDATE_RANGE_DAYS;
  };

  const confirmationMessage = makeDefaultSchedule
    ? `This will set your updated work hours starting on ${startDate?.format("MM/DD/YYYY")} and keep them as your default schedule moving forward.`
    : `This will set your updated work hours starting from ${startDate?.format("MM/DD/YYYY")} and ending on ${endDate?.format("MM/DD/YYYY")}.`;

  return (
    <Box>
      <Container
        maxWidth={false}
        sx={{
          width: { md: '50%' },
          margin: 'auto' // Centers the container
        }}
      >
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
            {currentStep < steps.length &&
              <Button
                variant="outlined"
                onClick={() => {
                  setCurrentStep((prev) => prev - 1);
                }
                }
                disabled={
                  currentStep === 0
                }
                sx={{ borderRadius: '4px', border: '2px solid #FFF', fontWeight: '600' }}
              >
                Back
              </Button>
            }

            {/* No next button in the last step*/}
            {currentStep < steps.length - 1 &&
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setCurrentStep((prev) => prev + 1)}
                sx={{ borderRadius: '4px', border: '2px solid rgba(255,255,255,0.15)', fontWeight: '600' }}
              >
                Next
              </Button>
            }

            {currentStep === steps.length - 1 &&
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setDialogOpen(true)}
                disabled={isSaving || isLoading}
                sx={{ borderRadius: '4px', border: '2px solid rgba(255,255,255,0.15)', fontWeight: '600' }}
              >
                {isSaving ? "Saving..." : "Save Schedule"}
              </Button>
            }
          </Box>
          <hr
            style={{
              border: "none",
              height: "2px",
              backgroundColor: "rgba(0,0,0,0.2)",
              boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
              marginBottom: "20px",
            }}
          />
        </Box>

        {/* Keep both steps mounted so card edit state persists when navigating */}
        <Box sx={{ display: currentStep === 0 ? 'block' : 'none' }}>
          <Typography
            variant="overline"
            align="center"
            display="block"
            sx={{ marginBottom: 2 }}
          >
            SET SCHEDULE
          </Typography>
          <Box>
            {Object.keys(parsedSchedule).length > 0 &&
              DAYS_OF_WEEK_PREFERRED_ORDER.map((day) => (
                <ScheduleCard
                  key={day}
                  day={day}
                  initialWorkHours={parsedSchedule[day]}
                  onScheduleChange={handleScheduleChange}
                  recurringBreaks={recurringBreaksByDay[day]}
                  onRecurringBreaksChange={handleRecurringBreaksChange}
                  templateEndDate={endDate}
                />
              ))}
          </Box>
        </Box>

        <Box sx={{ display: currentStep === 1 ? 'block' : 'none' }}>
          <Typography
            variant="overline"
            align="center"
            display="block"
            sx={{ marginBottom: 2 }}
          >
            SET START AND END DATE
          </Typography>
          <DropdownCalendar
            label={'Start Date'}
            date={startDate}
            onDateChange={onStartDateChange}
            shouldDisableDate={shouldDisableStartDate}
          />

          {!makeDefaultSchedule && (
            <>
              <DropdownCalendar
                label={'End Date'}
                date={endDate}
                onDateChange={onEndDateChange}
                shouldDisableDate={shouldDisableEndDate}
              />
              <Typography variant="body2" align="center" sx={{ mt: 1, mb: 3, color: 'text.secondary' }}>
                You can apply updates to at most {MAX_WORK_HOURS_UPDATE_RANGE_DAYS} days at a time.
              </Typography>
            </>

          )}

          <Typography variant="body1" fontStyle={'italic'} align='center'>
            Selecting this option will ensure these hours are stored and applied for future scheduling.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={makeDefaultSchedule}
                  onChange={(e) => onMakeDefaultScheduleChange(e.target.checked)}
                />
              }
              label="Make this my default hours"
            />
          </Box>
        </Box>

        {/* This is the same in the manageSchedule side */}
        <ConfirmationDialog
          open={dialogOpen}
          title="Confirm Changes"
          isLoading={isSaving}
          loadingLabel="Saving..."
          content={
            affectedAppointments.length > 0 && (
              <>
                <DialogContentText sx={{ mt: 2 }}>
                  The following appointments will be affected and will be cancelled:
                </DialogContentText>
                <ul>
                  {affectedAppointments.map((app, index) => {
                    const serviceName = app.appointmentTitle;

                    return (
                      <li key={index}>
                        {serviceName} with {app.customerFirstName} at{" "}
                        {convertTo12Hour(app.appointmentTime)} on {formatDate(app.appointmentDate)}
                      </li>
                    );
                  })}
                </ul>
                <DialogContentText sx={{ mt: 2 }}>
                  You can go back and change each appointment accordingly if you do
                  not want to cancel them.
                </DialogContentText>
              </>
            )
          }
          onConfirm={() => {
            if (!isSaving) {
              onSaveMutation.mutate();
            }
          }}
          onClose={() => setDialogOpen(false)}
          message={confirmationMessage}
        />
        <SuccessOrErrorDialog
          open={resultDialogOpen}
          onClose={onCloseResultDialog}
          success={onSaveMutation.isSuccess}
          successTitle={'Work Hours Changed!'}
          successMessage={"Your work hours have been updated!"}
          errorTitle={'Error Occured'}
          errorMessage={'Something went wrong, please try again.'} />
      </Container>
    </Box>
  );

};

export default EditWorkHoursPage;
